import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Trash2, Loader2, AlertTriangle, ShieldOff } from "lucide-react";

export function WishlistPanel() {
  const [items, setItems] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/wishlist"); setItems(data.projects || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (pid) => {
    try {
      await api.delete(`/wishlist/${pid}`);
      setItems((xs) => (xs || []).filter((x) => x.id !== pid));
      toast.success("Removed from saved list.");
    } catch { toast.error("Could not remove."); }
  };

  return (
    <div className="mv-card p-6" data-testid="wishlist-panel">
      <div className="flex items-center gap-2 mb-4">
        <Heart size={16} className="mv-green-text"/>
        <div className="font-display text-[16px] mv-green-text">Saved projects</div>
      </div>
      {items === null && (
        <div className="text-[13px] flex items-center gap-2" style={{ color: "var(--mv-muted)" }}>
          <Loader2 size={13} className="animate-spin"/> Loading…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="text-[13px]" style={{ color: "var(--mv-muted)" }} data-testid="wishlist-empty">
          Nothing saved yet. Browse <Link to="/projects" className="underline">projects</Link> and tap <em>Save for later</em>.
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3" data-testid={`wishlist-item-${p.id}`}>
              <div>
                <Link to={`/projects/${p.id}`} className="font-semibold hover:underline mv-green-text text-[14px]">{p.name}</Link>
                <div className="text-[12px]" style={{ color: "var(--mv-muted)" }}>
                  {p.country_flag} {p.city}, {p.country} · from {new Intl.NumberFormat("en-GB", { style: "currency", currency: (p.currency || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(p.price_from || 0)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(p.id)}
                aria-label={`Remove ${p.name} from saved list`}
                className="p-2 rounded-full hover:bg-black/[0.04]"
                data-testid={`wishlist-remove-${p.id}`}
              >
                <Trash2 size={14}/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AccountPanel() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    if (confirmText !== "DELETE") { toast.error("Type DELETE to confirm."); return; }
    setBusy(true);
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been deleted.");
      try { await logout(); } catch {}
      nav("/", { replace: true });
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mv-card p-6" data-testid="account-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff size={16} className="mv-green-text"/>
        <div className="font-display text-[16px] mv-green-text">Your account</div>
      </div>
      <p className="text-[13px]" style={{ color: "var(--mv-muted)" }}>
        Signed in as <strong>{user?.email}</strong>. You can permanently delete your account and personal data.
        Payment records will be anonymised for finance/audit purposes.
      </p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] border mv-hairline hover:bg-black/[0.04]"
          style={{ color: "#8a2020" }}
          data-testid="account-delete-open"
        >
          <Trash2 size={14}/> Delete my account
        </button>
      ) : (
        <div className="mt-4 border mv-hairline rounded-xl p-4" style={{ background: "rgba(212,60,60,0.05)" }} data-testid="account-delete-confirm">
          <div className="flex items-start gap-2 text-[13px]" style={{ color: "#8a2020" }}>
            <AlertTriangle size={14} className="mt-0.5"/>
            <div>
              This is permanent. Type <strong>DELETE</strong> below and click Confirm to remove your account.
            </div>
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            className="mt-3 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"
            data-testid="account-delete-input"
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={doDelete}
              disabled={busy || confirmText !== "DELETE"}
              className="btn-green"
              style={{ background: "#8a2020" }}
              data-testid="account-delete-confirm-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
              {busy ? "Deleting…" : "Confirm delete"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirmText(""); }}
              className="btn-ghost"
              data-testid="account-delete-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
